import { Component, type ReactNode } from "react";

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

/**
 * Last resort UI.
 *
 * A thrown render error should never become a blank `<div id="root">`: the
 * guest/host on a phone cannot tell "broken app" from "no connection", and a
 * reload is the only thing they need. We keep this intentionally dumb and free
 * of any data fetching so it cannot itself fail.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    console.error(JSON.stringify({ event: "ui_render_error", error: String(error) }));
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="layout"
        style={{ maxWidth: "30rem", margin: "4rem auto", textAlign: "center" }}
      >
        <h1>Something went wrong</h1>
        <p className="muted">
          The page crashed. Try reloading. If it keeps happening, tell the host.
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}